import React from 'react';
import { DataTableColumn } from 'mantine-datatable';

import { Request } from '../../../codegen/graphql';
import formatCurrency from '../../../utility/formatCurrency';
import { getCurrencyFromRequest } from '../../../utility/getProductCurrency';
import { SimpleHeader } from '../components/TableHeaderWrapper';
import {
    renderDueDate,
    renderImplementedDate,
    renderImplementedPrice,
    renderIsFromSurvey,
    renderMargin,
    renderProcurementManagers,
    renderSapStatus,
    renderSku,
    renderStatus,
    renderStoreDetails,
} from '../requestsTableRenderers';
import type { ColumnBuilderCtx } from '../BaseRequestsTable';
import { PriceChangeScopeBadge } from '../PriceChangeScopeBadge';

const CurrentPriceHeader = <SimpleHeader text="Current Price" />;
const ProposedPriceHeader = <SimpleHeader text="Proposed Price" />;
const MarginHeader = <SimpleHeader text="Margin" />;
const DueHeader = <SimpleHeader text="Due" />;
const DateImplementedHeader = <SimpleHeader text="Date Implemented" />;
const PriceImplementedHeader = <SimpleHeader text="Price Implemented" />;

export function getDefaultRequestsColumns(
    ctx: ColumnBuilderCtx,
): DataTableColumn<Request>[] {
    const { headers } = ctx;
    return [
        {
            accessor: 'friendlyId',
            title: headers.FriendlyId,
            width: 100,
            textAlign: 'center',
            noWrap: true,
            sortable: true,
        },
        {
            accessor: 'status',
            title: headers.Status,
            render: renderStatus,
            width: 100,
            textAlign: 'center',
            noWrap: true,
            sortable: false,
        },
        {
            accessor: 'sku',
            title: headers.Sku,
            render: renderSku,
            width: 225,
            noWrap: true,
            sortable: true,
        },
        {
            accessor: 'productCategory',
            title: headers.ProductCategory,
            render: ({ productCategory }) => productCategory ?? 'N/A',
            width: 200,
            noWrap: true,
            sortable: true,
        },
        {
            accessor: 'procurementManagers',
            title: headers.ProcurementManager,
            render: renderProcurementManagers,
            width: 150,
            sortable: true,
            noWrap: true,
        },
        {
            accessor: 'store',
            title: headers.Store,
            render: renderStoreDetails,
            sortable: true,
            width: 225,
            noWrap: true,
        },
        {
            accessor: 'advertisingZone',
            title: headers.AdvertisingZone,
            width: 200,
            render: ({ advertisingZoneName }) => advertisingZoneName ?? 'N/A',
            noWrap: true,
            sortable: true,
        },
        {
            accessor: 'isFromPriceSurvey',
            title: headers.Survey,
            render: renderIsFromSurvey,
            width: 120,
            textAlign: 'center',
            noWrap: true,
            sortable: false,
        },
        {
            accessor: 'priceChangeScope',
            title: headers.Scope,
            render: request => (
                <PriceChangeScopeBadge request={request} size="sm" />
            ),
            width: 140,
            textAlign: 'center',
            noWrap: true,
            sortable: false,
        },
        {
            accessor: 'currentPrice',
            title: CurrentPriceHeader,
            render: request => {
                const currency = getCurrencyFromRequest(request);
                return formatCurrency(request.currentPrice || 0, currency);
            },
            noWrap: true,
            width: 120,
        },
        {
            accessor: 'proposedPrice',
            title: ProposedPriceHeader,
            render: request => {
                const currency = getCurrencyFromRequest(request);
                return formatCurrency(request.proposedPrice || 0, currency);
            },
            noWrap: true,
            width: 160,
            sortable: true,
        },
        {
            accessor: 'margin',
            title: MarginHeader,
            render: renderMargin,
            noWrap: true,
        },
        {
            accessor: 'due',
            title: DueHeader,
            render: renderDueDate,
            sortable: true,
            noWrap: true,
        },
        {
            accessor: 'dateImplemented',
            title: DateImplementedHeader,
            render: renderImplementedDate,
            sortable: true,
            noWrap: true,
            width: 190,
        },
        {
            accessor: 'priceImplemented',
            title: PriceImplementedHeader,
            render: renderImplementedPrice,
            sortable: true,
            noWrap: true,
            width: 190,
        },
        {
            accessor: 'sapStatus',
            title: headers.SapOutcome,
            render: renderSapStatus,
            noWrap: true,
            width: 160,
        },
    ];
}
